import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useMemo } from 'react'

import type { PickerRow, UidGroupRow } from 'api/canonicalPicker.types'
import NotFoundUids from 'components/AI/CanonicalPicker/NotFoundUids'
import OutOfScopeUids from 'components/AI/CanonicalPicker/OutOfScopeUids'
import { Icon } from 'controlled-components'
import { buildTranslate } from 'locales'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'
import type { SourceSelection } from 'slices/aiSegmentBuilder/picker'

import CanonicalOptionsContainer from './CanonicalOptions.container'
import { unmatchedUidRowOf } from './pastedUids'
import type { UidPaste } from './useUidSearch'

const t = buildTranslate('components.ai.picker.canonical')

const SPINNER_SIZE = 24

const NO_GROUPS: UidGroupRow[] = []

type Props = {
  paste: UidPaste
  /** A typed UID nothing holds reads as no match, with one way to add it */
  isTyped?: boolean
  /** Groups the search found outside a scoped level, shown apart and never pickable */
  outOfScope?: UidGroupRow[]
  outOfScopeLabel?: string
  source: PickerSource
  selection: SourceSelection
  isRetrying: boolean
  onRetry: () => void
  onPick: (row: PickerRow) => void
  onPickGroup: (group: UidGroupRow) => void
  /** Adds picks without opening the list's rows, for uids nothing holds */
  onAdd: (rows: PickerRow[]) => void
}

/**
 * What typed or pasted uids find: the groups that hold them, marked and
 * opened where a store UID matched, with nothing ticked, and then the uids
 * nothing holds.
 */
const UidSearchResultsContainer = ({
  paste,
  isTyped = false,
  outOfScope = NO_GROUPS,
  outOfScopeLabel = '',
  source,
  selection,
  isRetrying,
  onRetry,
  onPick,
  onPickGroup,
  onAdd,
}: Props) => {
  const highlighted = useMemo(() => new Set(paste.uids), [paste.uids])

  if (paste.status === 'failed') {
    return (
      <div role="alert" className="p-m text-center">
        <Text.BodyText4 tag="p" color="grey70">
          {t('paste.failed')}
        </Text.BodyText4>
        <Button kind="minimal" isLoading={isRetrying} onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    )
  }
  if (paste.status === 'checking') {
    return (
      <div role="status" className="p-m text-center">
        <Icon name="spinner" size={SPINNER_SIZE} spin aria-hidden="true" />
        <span className="sr-only">
          {t('paste.checking', { count: paste.uids.length })}
        </span>
      </div>
    )
  }

  const notFound = paste.unmatched.map(unmatchedUidRowOf)
  const notAdded = notFound.filter(row => !(row.id in selection.rows))

  if (isTyped && paste.groups.length === 0 && outOfScope.length === 0) {
    const [typed] = paste.uids
    const added = notFound.flatMap(row => selection.rows[row.id] ?? [])
    // Once added, the typed UID shows as the pick it made, which can be unticked
    if (added.length > 0) {
      return (
        <CanonicalOptionsContainer
          source={source}
          rows={added}
          selection={selection}
          isMultiselect
          onPick={onPick}
          onPickGroup={onPickGroup}
        />
      )
    }
    return (
      <div role="status" className="p-m text-center">
        <Text.BodyText4 tag="p" color="grey70">
          {t('no_uid_matches')}
        </Text.BodyText4>
        <Button
          kind="minimal"
          size="small"
          aria-label={t('paste.add_label', { uid: typed })}
          onClick={() => onAdd(notAdded)}
        >
          {t('paste.add')}
        </Button>
      </div>
    )
  }

  return (
    <>
      {paste.pastedCount > paste.uids.length && (
        <div role="status" className="px-m pt-s">
          <Text.BodyText5 color="grey70">
            {t('paste.capped', {
              checked: paste.uids.length,
              pasted: paste.pastedCount,
            })}
          </Text.BodyText5>
        </div>
      )}
      <CanonicalOptionsContainer
        source={source}
        rows={paste.groups}
        selection={selection}
        isMultiselect
        highlightedUids={highlighted}
        onPick={onPick}
        onPickGroup={onPickGroup}
      />
      {outOfScope.length > 0 && (
        <OutOfScopeUids label={outOfScopeLabel} groups={outOfScope} />
      )}
      {notFound.length > 0 && (
        <NotFoundUids
          uids={notFound.map(row => ({
            uid: row.uid,
            isAdded: row.id in selection.rows,
          }))}
          onAdd={uid => onAdd([unmatchedUidRowOf(uid)])}
          onAddAll={() => onAdd(notAdded)}
        />
      )}
    </>
  )
}

export default UidSearchResultsContainer
